import { useFormikContext } from 'formik';
import { useDictionaryValues } from 'services/api/queries';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { CountryModel, CurrencySelectModel } from 'services/EpdClient';
import { useTranslation } from 'react-i18next';
import calculateCurrencyOptions from './calculateCurrencyOptions';
import { StyledReactSelect } from 'styles/Styles.styled';
import { Option } from 'types/types';
import { DropdownIndicator } from 'components/form/DropdownIndicator';
import SelectDataField from 'components/v2/form/select-data-field/SelectDataField';

interface AvailableCountryCurrenciesProps {
  countries: CountryModel[];
  /** Legacy react-select styles; when omitted, renders using the shared SelectDataField design. */
  styles?: any;
  tooltip?: string | undefined;
  enableTooltipError?: boolean | undefined;
  supressErrors?: boolean | undefined;
  isRequired: boolean;
  isDisabled?: boolean;
}

const AvailableCountryCurrencies: React.FC<AvailableCountryCurrenciesProps> = ({
  countries,
  styles,
  tooltip,
  enableTooltipError,
  supressErrors,
  isRequired,
  isDisabled,
}) => {
  const { values: formValues, setFieldValue } = useFormikContext<CurrencySelectModel>();
  const eurCurrencyCountries = useDictionaryValues(EPD_DICTIONARY_CODES.EUR_CURRENCY_COUNTRIES);
  const { t } = useTranslation();
  const currencyOptions = (() => {
    const countryId = formValues.invoiceCountryOption?.value;
    if (!countryId || !eurCurrencyCountries.data) return [];
    const countryAplha2Code = countries.find((c) => c.id === countryId)?.alpha2Code;
    if (!countryAplha2Code) return [];
    return calculateCurrencyOptions(
      countryAplha2Code,
      eurCurrencyCountries.data.map((c) => c.value)
    ) as Option[];
  })();
  const isSelectDisabled = currencyOptions.length === 1 || isDisabled;

  if (!styles) {
    return (
      <SelectDataField
        label={t('invoicePreferredCurrency')}
        placeholder={t('placeholder.select')}
        name="preferredCurrencyOption"
        tooltip={tooltip}
        required={isRequired}
        isMulti={false}
        disabled={isSelectDisabled}
        value={formValues.preferredCurrencyOption}
        onChange={(value: Option | null) => setFieldValue('preferredCurrencyOption', value)}
        options={currencyOptions}
        shouldShowBottomError={(err) => err !== t('messages.required')}
      />
    );
  }

  return (
    <StyledReactSelect
      required={isRequired}
      label={t('invoicePreferredCurrency')}
      name="preferredCurrencyOption"
      tooltip={tooltip}
      isDisabled={isSelectDisabled}
      placeholder={t('placeholder.select')}
      styles={styles}
      options={currencyOptions}
      enableTooltipError={enableTooltipError}
      supressErrors={supressErrors}
      components={{ DropdownIndicator }}
    />
  );
};

export default AvailableCountryCurrencies;
